import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditTrailPanel = ({ contracts }) => {
  const [filterType, setFilterType] = useState('all');

  // Generate audit trail entries from contracts
  const generateAuditTrail = () => {
    const entries = [];
    
    contracts?.forEach((contract) => {
      entries?.push({
        id: `create-${contract?.id}`,
        contractId: contract?.id,
        employeeName: contract?.employee ? `${contract?.employee?.firstName} ${contract?.employee?.lastName}` : 'Unknown',
        employeeNumber: contract?.employee?.employeeNumber,
        action: 'created',
        actionType: 'creation',
        contractType: contract?.contractType,
        version: contract?.versionNumber || 1,
        timestamp: contract?.createdAt,
        details: `Contract created - ${contract?.contractType?.replace('_', ' ')}`,
        user: 'System Admin'
      });

      if (contract?.updatedAt && contract?.updatedAt !== contract?.createdAt) {
        entries?.push({
          id: `update-${contract?.id}`,
          contractId: contract?.id,
          employeeName: contract?.employee ? `${contract?.employee?.firstName} ${contract?.employee?.lastName}` : 'Unknown',
          employeeNumber: contract?.employee?.employeeNumber,
          action: 'modified',
          actionType: 'modification',
          contractType: contract?.contractType,
          version: contract?.versionNumber || 1,
          timestamp: contract?.updatedAt,
          details: 'Contract terms updated',
          user: 'HR Manager'
        });
      }

      if (contract?.contractStatus === 'active') {
        entries?.push({
          id: `approve-${contract?.id}`,
          contractId: contract?.id,
          employeeName: contract?.employee ? `${contract?.employee?.firstName} ${contract?.employee?.lastName}` : 'Unknown',
          employeeNumber: contract?.employee?.employeeNumber,
          action: 'approved',
          actionType: 'approval',
          contractType: contract?.contractType,
          version: contract?.versionNumber || 1,
          timestamp: contract?.updatedAt || contract?.createdAt,
          details: 'Contract approved and activated',
          user: 'Legal Department'
        });
      }
    });

    return entries?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp));
  };

  const auditEntries = generateAuditTrail();
  const filteredEntries = filterType === 'all' 
    ? auditEntries 
    : auditEntries?.filter(entry => entry?.actionType === filterType);

  const getActionIcon = (action) => {
    switch (action) {
      case 'created': return 'FilePlus';
      case 'modified': return 'Edit';
      case 'approved': return 'CheckCircle';
      case 'renewed': return 'RefreshCw';
      default: return 'FileText';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created': return 'text-blue-600 bg-blue-50';
      case 'modified': return 'text-amber-600 bg-amber-50';
      case 'approved': return 'text-green-600 bg-green-50';
      case 'renewed': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Contract Audit Trail</h3>
          <p className="text-sm text-gray-600 mt-1">Complete history of contract modifications and approvals</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <Icon name="Download" className="h-4 w-4" />
          Export Audit Log
        </Button>
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'All Actions' },
          { id: 'creation', label: 'Creations' },
          { id: 'modification', label: 'Modifications' },
          { id: 'approval', label: 'Approvals' }
        ]?.map((filter) => (
          <button
            key={filter?.id}
            onClick={() => setFilterType(filter?.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filterType === filter?.id
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter?.label}
          </button>
        ))}
      </div>

      {/* Audit Timeline */}
      <div className="space-y-3">
        {filteredEntries?.length > 0 ? (
          filteredEntries?.map((entry) => (
            <div key={entry?.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-lg ${getActionColor(entry?.action)}`}>
                  <Icon name={getActionIcon(entry?.action)} className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {entry?.employeeName} ({entry?.employeeNumber})
                      </h4>
                      <p className="text-sm text-gray-600">{entry?.details}</p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                      {new Date(entry?.timestamp)?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Icon name="FileText" className="h-3 w-3" />
                      {entry?.contractType?.replace('_', ' ')?.toUpperCase()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Hash" className="h-3 w-3" />
                      Version {entry?.version}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="User" className="h-3 w-3" />
                      {entry?.user}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Icon name="History" className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>No audit trail entries found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditTrailPanel;